    import { useAxios} from '../utils/axiosConfig';
    import { useNotification } from "../contexts/NotificationContext";
    import { getErrorMessage } from '../utils/errorMessageUtils';
    import { AuditEntriesModel } from 'components/History Tracking/AuditComponent';
    import config from '../contexts/ConfigContext';


    export interface AnnotationLabelModel{   
        AnnotationId: number;
        LabelId: number;  
        Label: string;
    }

    export interface AnnotationModel{
        Id: number;
        DocumentId: number;
        EntityId: number;
        Entity: string;
        StatusId: number;
        IsFlagged: boolean;    
        AnnotationLabels: AnnotationLabelModel[];
    }

    export interface AnnotationEntityModel{
        EntityDescription: string
        EntityId: number
        HasChildren: boolean
        Id: number
        LabelDescription: string
        LabelId: number
        ParentId: number
        ProjectId: number
    }
    export class AnnotationService {

        axios = useAxios();

        // Define the notification hook
        notification = useNotification();


        async GetAnnotationLabels(annotationId: number): Promise<AnnotationModel>{
            try {
                const response = await this.axios.get(config.API_LABELING_URL + "/api/Annotations/GetAnnotationLabels?annotationId= " + annotationId);
                return response.data;
            }
            catch (error: any) {
                this.notification.showNotification("Error getting annotation labels: " + error, 'error');
                console.error("Error getting annotation labels: ", error);
                return {} as AnnotationModel;
            }
        }
        async GetAnnotationsEntityLabels(entityId: number, labelId?: number, childLabelId?: number, thirdLabelId?: number): Promise<AnnotationEntityModel[]>{
            try {
                const response = await this.axios.get(config.API_LABELING_URL + "/api/AnnotationEntityLabels?entityId=" + entityId);
                // Restore metadata for every selected level, without repeating a row
                // when IDs repeat. Missing IDs must not select unrelated labels.
                const selectedIds = new Set([labelId, childLabelId, thirdLabelId].filter(
                    (id): id is number => id !== undefined && Number.isInteger(id) && id > 0
                ));
                return response.data.filter((element: AnnotationEntityModel) => selectedIds.has(element.LabelId));
            }
            catch (error: any) {
                this.notification.showNotification("Error getting annotation labels: " + error, 'error');
                console.error("Error getting annotation labels: ", error);
                return [] as AnnotationEntityModel[];
            }
        }
        /*async GetAnnotations(documentId: number): Promise<AnnotationModel[]>{
            try {
                const response = await this.axios.get(config.API_LABELING_URL + "/odata/Annotations");
                return response.data;
            }
            catch (error) {
                this.notification.showNotification("Error getting annotations: " + error, 'error');
                console.error("Error getting annotations: ", error);
                return [];
            }
        }*/
    async GetAnnotationStatuses(): Promise<any>{
            try {
                const response = await this.axios.get(config.API_LABELING_URL + "/api/Annotations/GetAnnotationStatus");
                return response.data;
            }
            catch (error) {
                this.notification.showNotification("Error getting annotation statuses: " + error, 'error');
                console.error("Error getting annotation statuses: ", error);
                return [];
            }
        }

        async CheckDocumentStatus(documentId: number): Promise<any>{
            try {
                const response = await this.axios.get(config.API_LABELING_URL + "/api/Documents/CheckDocumentStatus?documentId= " + documentId);
                return response.data;
            }
            catch (error) {
                this.notification.showNotification("Error getting document status: " + error, 'error');
                console.error("Error getting document status: ", error);
                return null;
            }
        }

        async SetDocumentStatus(documentId: number, statusId: number): Promise<any>{
            try {
                const response = await this.axios.put(config.API_LABELING_URL + "/api/Documents/SetDocumentStatus?statusId=" + statusId, documentId);
                return response.data;
            }
            catch (error: unknown) {

                this.notification.showNotification(getErrorMessage(error), 'error');
                return null;
            }
        }

        async AddAnnotation(annotation: AnnotationModel): Promise<any> {
            try {
                const response = await this.axios.post(config.API_LABELING_URL + "/api/Annotations/", annotation);
                return response.data;
            }
            catch (error: unknown) {

                this.notification.showNotification(getErrorMessage(error), 'error');
                return null;
            }
        }

        async UpdateAnnotation(annotation: AnnotationModel): Promise<any> {
            try {
                const response = await this.axios.put(config.API_LABELING_URL + "/api/Annotations/", annotation);
                return response.data;
            }
            catch (error: unknown) {

                this.notification.showNotification(getErrorMessage(error), 'error');
                return null;
            }
        }
        
        async SetAnnotationStatus(annotationId: number, statusId: number): Promise<any> {
            try {                
                const response = await this.axios.put(config.API_LABELING_URL + "/api/Annotations/SetAnnotationStatus?statusId=" + statusId, annotationId);
                return response.data;
            }
            catch (error: unknown) {

                this.notification.showNotification(getErrorMessage(error), 'error');
                return null;
            }
        }

        async SetQAReview(annotationId: number, checkedQAReview: boolean): Promise<any> {
            try {                
                const response = await this.axios.put(config.API_LABELING_URL + "/api/Annotations/SetQAReview?checkedQAReview=" + checkedQAReview, annotationId);
                return response.data;
            }
            catch (error: any) {
                if(error?.response?.status === 422){
                    this.notification.showNotification("Error setting QA review: " + error.response.data?.detail, 'warning');
                }
                else{
                    this.notification.showNotification("Error setting annotation QAReview: " + error, 'error');
                }
                console.error("Error setting annotation annotation QAReview: ", error);
                throw error;
            }
        }

        async DeleteAnnotation(annotationId: number): Promise<any> {
            try {
                const response = await this.axios.delete(config.API_LABELING_URL + "/api/Annotations?annotationId=" + annotationId);
                return response.data;
            }
            catch (error) {
                this.notification.showNotification("Error deleting annotation: " + error, 'error');
                console.error("Error deleting annotation: ", error);
                return null;
            }
        }

        async CloneAnnotation(annotationId: number): Promise<any> {
            try {
                const response = await this.axios.post(config.API_LABELING_URL + "/api/Annotations/Clone?annotationId=" + annotationId);
                return response.data;
            }
            catch (error) {
                this.notification.showNotification("Error cloning annotation: " + error, 'error');
                console.error("Error cloning annotation: ", error);
                return null;
            }
        }

        async SetIsFlagged(annotationId: number, isFlagged: boolean): Promise<any> {
            try {                
                const response = await this.axios.put(config.API_LABELING_URL + "/api/Annotations/SetIsFlagged?isFlagged=" + isFlagged, annotationId);
                return response.data;
            }
            catch (error) {
                this.notification.showNotification("Error setting the need help value: " + error, 'error');
                console.error("Error setting need help value: ", error);
                return null;
            }
        }

        async GetAnnotationMetadata(annotationId: number): Promise<any>{
            try {
                const response = await this.axios.get(config.API_LABELING_URL + "/api/Annotations/GetAnnotationMetadata?annotationId=" + annotationId);
                return response.data;
            }
            catch (error: any) {
                this.notification.showNotification("Error getting annotation metadata: " + error, 'error');
                console.error("Error getting annotation metadata: ", error);
                return {};
            }
        }

        async GetAudit(annotationId: number): Promise<AuditEntriesModel[]>{
            try {
                const response = await this.axios.get(config.API_LABELING_URL + "/api/Annotations/GetAudit?annotationId=" + annotationId);
                return response.data;
            }
            catch (error: any) {
                this.notification.showNotification("Error getting annotation audit history: " + error, 'error');
                console.error("Error getting annotation audit history: ", error);
                return {} as AuditEntriesModel[];
            }
        }
    }